
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

interface CreatorTermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function CreatorTermsDialog({
  open,
  onOpenChange,
  onConfirm
}: CreatorTermsDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Creator Terms of Service</AlertDialogTitle>
        </AlertDialogHeader>
        
        <ScrollArea className="h-[60vh] mt-4 pr-4">
          <AlertDialogDescription className="space-y-4 text-sm leading-relaxed">
            <p>
              Welcome to Airfryer.ai! Before you apply to become a creator, please carefully read and understand our terms of service.
            </p>

            <h3 className="font-semibold text-base text-foreground">Content Ownership & Copyright</h3>
            <p>
              By applying as a creator, you confirm that all recipes and content you publish will be your own original work. 
              Copying or plagiarizing content from other sources is strictly prohibited and may result in permanent account suspension.
            </p>

            <h3 className="font-semibold text-base text-foreground">Future Platform Changes</h3>
            <p>
              We are committed to creating a sustainable platform that benefits both creators and users. As we grow, we may implement the following changes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Introduction of a universal subscription model that gives users access to premium content across the platform</li>
              <li>Performance-based earnings distribution based on content engagement and viewer metrics</li>
              <li>Revenue sharing from universal subscriptions among active creators</li>
            </ul>

            <h3 className="font-semibold text-base text-foreground">Revenue Sharing & Commissions</h3>
            <p>
              Our current commission structure is designed to maintain and improve the platform while fairly compensating creators:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Platform fee: 30% of revenue
                <ul className="list-disc pl-6 mt-2">
                  <li>10% allocated to affiliate partners who help promote content</li>
                  <li>20% for platform maintenance, development, and improvements</li>
                </ul>
              </li>
              <li>Creator earnings: 70% of revenue</li>
            </ul>

            <h3 className="font-semibold text-base text-foreground">Future Creator Benefits</h3>
            <p>
              We believe in sharing our success with our creators. As the platform matures, we plan to implement additional benefits:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Potential platform ownership opportunities through airdrops or cash distributions</li>
              <li>Enhanced revenue sharing models</li>
              <li>Additional creator-exclusive features and tools</li>
            </ul>

            <p className="text-muted-foreground italic mt-4">
              Note: These terms may be updated as the platform evolves. We will always strive to make decisions that benefit our creator community while maintaining platform sustainability.
            </p>
          </AlertDialogDescription>
        </ScrollArea>

        <AlertDialogFooter className="mt-6">
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button onClick={onConfirm}>Accept & Apply</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
